
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { generateTimeSlots, parseStoredDateTime } from "@/utils/timeUtils";

export const useBookings = (serviceId: string | undefined, selectedDate: Date) => {
  const { toast } = useToast();
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [blockedTimes, setBlockedTimes] = useState<string[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const allTimeSlots = generateTimeSlots();

  const fetchBookings = async () => {
    try {
      if (!serviceId) return;
      setLoading(true);
      
      const svcId = parseInt(serviceId);
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);

      // Get all bookings for the service with service details (exclude removed bookings from admin calendar view)
      const { data: allBookings, error: bookingsError } = await supabase
        .from('bookings')
        .select(`
          *,
          services:service_id (
            name,
            price,
            region,
            regions:region (
              currency
            )
          )
        `)
        .eq('service_id', svcId)
        .not('status', 'eq', 'removed by admin')
        .order('start_time', { ascending: false });

      if (bookingsError) throw bookingsError;

      // Get bookings for the selected day
      const { data: dayBookings, error: dayBookingsError } = await supabase
        .from('bookings')
        .select(`
          *,
          services:service_id (
            name,
            price,
            region,
            regions:region (
              currency
            )
          )
        `)
        .eq('service_id', svcId)
        .gte('start_time', startOfDay.toISOString())
        .lte('start_time', endOfDay.toISOString())
        .not('status', 'in', '("rejected","cancelled","removed by admin")');

      if (dayBookingsError) throw dayBookingsError;

      const blockedSlots: string[] = [];
      const bookedSlots = new Set<string>();

      dayBookings?.forEach(booking => {
        const startTime = parseStoredDateTime(booking.booking_date || booking.start_time, booking.start_time_local);
        const endTime = parseStoredDateTime(booking.booking_date || booking.end_time, booking.end_time_local);
        
        let currentTime = new Date(startTime);
        while (currentTime < endTime) {
          const timeSlot = format(currentTime, 'HH:mm');
          if (booking.status === 'blocked' || booking.status === 'travel') {
            blockedSlots.push(timeSlot);
          } else {
            bookedSlots.add(timeSlot);
          }
          currentTime.setMinutes(currentTime.getMinutes() + 30);
        }
      });

      // Transform bookings to include needed service info
      const transformedBookings = allBookings?.map(booking => {
        const serviceData = booking.services || {};
        const regionData = serviceData.regions || {};
        
        return {
          ...booking,
          service_name: serviceData.name,
          service_price: serviceData.price,
          currency: regionData.currency || 'Ksh' // Provide a default currency if missing
        };
      }) || [];

      setBlockedTimes(blockedSlots);
      setBookings(transformedBookings); 
      
      const available = allTimeSlots.filter(slot => 
        !bookedSlots.has(slot) && !blockedSlots.includes(slot)
      );
      setAvailableTimeSlots(available);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load bookings",
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (serviceId) {
      fetchBookings();
    }
  }, [selectedDate, serviceId]);

  return {
    bookings,
    loading,
    blockedTimes,
    availableTimeSlots,
    fetchBookings
  };
};
